import React, { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import DatePicker from './DatePicker';
import {
  FileText, File, Download, Eye, Upload, FolderOpen,
  History as HistoryIcon, ChevronDown, ChevronUp,
  Sparkles, CheckCircle2, AlertTriangle, XCircle, HelpCircle, Loader2
} from 'lucide-react';
import { uploadLicenseFile, validateAttachmentWithAI } from '../services/api';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const formatDateTime = (s) => {
  try { return new Date(s).toLocaleString('pt-BR'); } catch { return s; }
};


const VERDICTS = {
  valido:     { label: 'Conferido pela IA: sem divergências', icon: CheckCircle2, cls: 'bg-green-50 border-green-200 text-green-800' },
  atencao:    { label: 'Pontos de atenção encontrados',       icon: AlertTriangle, cls: 'bg-amber-50 border-amber-200 text-amber-800' },
  divergente: { label: 'Divergências com o cadastro',         icon: XCircle,       cls: 'bg-red-50 border-red-200 text-red-800' },
  ilegivel:   { label: 'Documento ilegível ou não reconhecido', icon: HelpCircle,  cls: 'bg-gray-50 border-gray-300 text-gray-700' },
};
const SEVERITY_DOT = { error: 'bg-red-500', warning: 'bg-amber-500', info: 'bg-blue-400' };
const FIELD_LABEL = {
  type: 'Tipo', cnpj: 'CNPJ', license_number: 'Número', issuing_authority: 'Órgão emissor',
  issue_date: 'Emissão', expiry_date: 'Vencimento', documento: 'Documento',
};

const AIValidationPanel = ({ validation }) => {
  const v = VERDICTS[validation.verdict] || VERDICTS.ilegivel;
  const Icon = v.icon;
  const ex = validation.extracted || {};
  return (
    <div className={`rounded-lg border p-4 space-y-3 ${v.cls}`} data-testid="ai-validation-panel">
      <div className="flex items-center gap-2 font-semibold">
        <Icon className="w-5 h-5" /> {v.label}
      </div>
      {ex.resumo && <p className="text-sm">{ex.resumo}</p>}
      {validation.findings?.length > 0 && (
        <ul className="space-y-1.5 text-sm" data-testid="ai-findings">
          {validation.findings.map((f, i) => (
            <li key={i} className="flex gap-2">
              <span className={`mt-1.5 w-2 h-2 rounded-full flex-shrink-0 ${SEVERITY_DOT[f.severity] || 'bg-gray-400'}`} />
              <span>
                <strong>{FIELD_LABEL[f.field] || f.field}:</strong> {f.message}
                {(f.expected || f.found) && (
                  <span className="block text-xs opacity-80">
                    {f.expected ? `Cadastro: ${f.expected}` : ''}{f.expected && f.found ? ' · ' : ''}{f.found ? `Documento: ${f.found}` : ''}
                  </span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
      <p className="text-xs opacity-70">
        {validation.disclaimer} {validation.validated_at ? `· ${formatDateTime(validation.validated_at)}` : ''}
      </p>
    </div>
  );
};

const DocTab = ({ license, kind, items, canEdit, onChanged }) => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [issueDate, setIssueDate] = useState(license.issue_date ? license.issue_date.split('T')[0] : '');
  const [expiryDate, setExpiryDate] = useState(license.expiry_date ? license.expiry_date.split('T')[0] : '');
  const [indet, setIndet] = useState(!!license.indeterminate_expiry);
  const [aiOnUpload, setAiOnUpload] = useState(true);
  const [validating, setValidating] = useState(false);
  const inputRef = useRef(null);

  const latest = items[0];
  const older = items.slice(1);
  const label = kind === 'protocolo' ? 'protocolo' : 'arquivo';

  const handleUpload = async () => {
    if (!file) { toast.error('Selecione um arquivo primeiro'); return; }
    setUploading(true);
    try {
      const opts = { kind };
      if (kind === 'arquivo') {
        if (issueDate) opts.issue_date = issueDate;
        opts.indeterminate_expiry = indet;
        if (!indet && expiryDate) opts.expiry_date = expiryDate;
      }
      const { data: uploaded } = await uploadLicenseFile(license.id, file, opts);
      toast.success(kind === 'protocolo' ? 'Protocolo enviado com sucesso' : 'Arquivo enviado com sucesso');
      setFile(null);
      if (inputRef.current) inputRef.current.value = '';
      onChanged?.();
      if (kind === 'arquivo' && aiOnUpload && uploaded?.id) {
        await runValidation(uploaded.id);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao enviar arquivo');
    } finally {
      setUploading(false);
    }
  };

  const runValidation = async (attachmentId) => {
    setValidating(true);
    try {
      const { data } = await validateAttachmentWithAI(license.id, attachmentId);
      const msg = { valido: 'IA: documento conferido, sem divergências', atencao: 'IA: há pontos de atenção',
                    divergente: 'IA: divergências encontradas', ilegivel: 'IA: documento ilegível' }[data.verdict];
      (data.verdict === 'valido' ? toast.success : toast.warning)(msg);
      onChanged?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erro ao validar com IA');
    } finally {
      setValidating(false);
    }
  };

  return (
    <div className="space-y-4">
      {latest ? (
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200" data-testid={`latest-${kind}`}>
          <div className="p-3 bg-blue-50 rounded-lg self-start">
            <FileText className="w-6 h-6 text-[#003366]" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-gray-900 font-semibold truncate">{latest.file_name}</p>
            <p className="text-xs text-gray-500">
              Versão mais recente · {formatDateTime(latest.uploaded_at)}
              {latest.uploaded_by_name ? ` · ${latest.uploaded_by_name}` : ''}
            </p>
          </div>
          <div className="flex items-center gap-2">
            <a
              href={`${BACKEND}${latest.file_url}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-3 py-2 bg-white text-gray-700 border border-gray-300 rounded-md hover:bg-gray-100 text-sm transition-colors"
              data-testid={`view-${kind}-btn`}
            >
              <Eye className="w-4 h-4" /> Visualizar
            </a>
            <a
              href={`${BACKEND}${latest.file_url}?download=1`}
              className="flex items-center gap-2 px-3 py-2 bg-[#003366] hover:bg-[#002244] text-white rounded-md text-sm transition-colors shadow-sm"
              data-testid={`download-${kind}-btn`}
            >
              <Download className="w-4 h-4" /> Baixar
            </a>
          </div>
        </div>
      ) : (
        <div className="text-center py-6 text-gray-400 border border-dashed border-gray-200 rounded-lg" data-testid={`empty-${kind}`}>
          <FolderOpen className="w-8 h-8 mx-auto mb-2 opacity-50" />
          Nenhum {label} enviado ainda
        </div>
      )}

      {latest && kind === 'arquivo' && (
        <div className="space-y-3" data-testid="ai-validation-section">
          {canEdit && (
            <Button type="button" variant="outline" onClick={() => runValidation(latest.id)} disabled={validating}
              className="bg-white text-[#003366] border-[#003366]/30 text-sm" data-testid="validate-ai-btn">
              {validating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {validating ? 'Lendo documento...' : latest.ai_validation ? 'Validar novamente com IA' : 'Validar com IA'}
            </Button>
          )}
          {latest.ai_validation && <AIValidationPanel validation={latest.ai_validation} />}
        </div>
      )}

      {older.length > 0 && (
        <div>
          <button
            type="button"
            onClick={() => setShowHistory(!showHistory)}
            className="text-sm text-[#003366] flex items-center gap-1 hover:underline"
            data-testid={`history-toggle-${kind}`}
          >
            <HistoryIcon className="w-4 h-4" />
            {showHistory ? 'Ocultar histórico' : `Ver histórico (${older.length} versão${older.length > 1 ? 'ões' : ''})`}
            {showHistory ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
          {showHistory && (
            <div className="mt-2 space-y-1.5" data-testid={`history-list-${kind}`}>
              {older.map((o) => (
                <div key={o.id} className="flex items-center gap-3 p-2 bg-white rounded border border-gray-100 text-sm">
                  <File className="w-4 h-4 text-gray-400 flex-shrink-0" />
                  <span className="flex-1 truncate text-gray-700">{o.file_name}</span>
                  <span className="text-xs text-gray-400 hidden sm:inline">{formatDateTime(o.uploaded_at)}</span>
                  <a
                    href={`${BACKEND}${o.file_url}?download=1`}
                    className="text-[#003366] hover:underline text-xs flex items-center gap-1"
                    data-testid={`history-download-${o.id}`}
                  >
                    <Download className="w-3.5 h-3.5" /> Baixar
                  </a>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {canEdit && (
        <div className="pt-4 border-t border-gray-100 space-y-3">
          <p className="text-sm font-semibold text-gray-700 flex items-center gap-1">
            <Upload className="w-4 h-4" /> Enviar nova versão do {label}
          </p>
          {kind === 'arquivo' && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Data de Emissão</Label>
                <DatePicker value={issueDate} onChange={setIssueDate} placeholder="Emissão" data-testid="upload-issue-date" />
              </div>
              <div className="space-y-1">
                <Label className="text-xs text-gray-600">Data de Vencimento</Label>
                <DatePicker value={expiryDate} onChange={setExpiryDate} placeholder="Vencimento" disabled={indet} data-testid="upload-expiry-date" />
              </div>
              <label className="flex items-center gap-2 text-sm text-gray-600 md:col-span-2 cursor-pointer">
                <Checkbox checked={indet} onCheckedChange={(v) => setIndet(!!v)} data-testid="upload-indeterminate" />
                Prazo indeterminado (sem vencimento)
              </label>
            </div>
          )}
          <div className="flex items-center gap-2 flex-wrap">
            <input
              ref={inputRef}
              type="file"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              className="hidden"
              data-testid={`file-input-${kind}`}
            />
            <Button type="button" variant="outline" onClick={() => inputRef.current?.click()} className="bg-white text-gray-700 border-gray-300 text-sm" data-testid={`pick-file-${kind}`}>
              <Upload className="w-4 h-4 mr-2" /> Escolher arquivo
            </Button>
            {file && <span className="text-sm text-gray-600 truncate max-w-[220px]">{file.name}</span>}
            <Button type="button" onClick={handleUpload} disabled={uploading || !file} className="bg-[#003366] hover:bg-[#002244] text-sm" data-testid={`upload-btn-${kind}`}>
              {uploading ? 'Enviando...' : 'Enviar'}
            </Button>
          </div>
          {kind === 'arquivo' && (
            <label className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer">
              <Checkbox checked={aiOnUpload} onCheckedChange={(v) => setAiOnUpload(!!v)} data-testid="ai-on-upload" />
              <Sparkles className="w-4 h-4 text-[#003366]" /> Validar com IA ao enviar (PDF ou imagem)
            </label>
          )}
          {kind === 'arquivo' && (
            <p className="text-xs text-gray-400">Ao enviar, as datas acima atualizam o alvará.</p>
          )}
        </div>
      )}
    </div>
  );
};

const LicenseDocuments = ({ license, canEdit, onChanged }) => {
  const atts = Array.isArray(license.attachments) ? [...license.attachments] : [];
  if (license.file_url && !atts.some((a) => a.file_url === license.file_url)) {
    atts.push({
      id: 'legacy',
      kind: 'arquivo',
      file_name: license.file_name || 'Arquivo anexado',
      file_url: license.file_url,
      uploaded_at: license.updated_at,
    });
  }
  const byKind = (k) =>
    atts
      .filter((a) => (a.kind || 'arquivo') === k)
      .sort((a, b) => new Date(b.uploaded_at || 0) - new Date(a.uploaded_at || 0));
  const arquivos = byKind('arquivo');
  const protocolos = byKind('protocolo');

  return (
    <Card className="bg-white border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300" data-testid="license-documents-card">
      <CardHeader className="border-b border-gray-100">
        <CardTitle className="text-lg text-gray-900 flex items-center gap-2 font-semibold">
          <FolderOpen className="w-5 h-5 text-[#003366]" />
          Documentos
        </CardTitle>
      </CardHeader>
      <CardContent className="p-6">
        <Tabs defaultValue="arquivo">
          <TabsList className="bg-gray-100">
            <TabsTrigger value="arquivo" data-testid="tab-arquivo">
              Arquivo {arquivos.length > 0 && <Badge variant="outline" className="ml-2 bg-white text-gray-600 border-gray-300">{arquivos.length}</Badge>}
            </TabsTrigger>
            <TabsTrigger value="protocolo" data-testid="tab-protocolo">
              Protocolo {protocolos.length > 0 && <Badge variant="outline" className="ml-2 bg-white text-gray-600 border-gray-300">{protocolos.length}</Badge>}
            </TabsTrigger>
          </TabsList>
          <TabsContent value="arquivo" className="mt-4">
            <DocTab license={license} kind="arquivo" items={arquivos} canEdit={canEdit} onChanged={onChanged} />
          </TabsContent>
          <TabsContent value="protocolo" className="mt-4">
            <DocTab license={license} kind="protocolo" items={protocolos} canEdit={canEdit} onChanged={onChanged} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default LicenseDocuments;
